import { TextFieldProps, TextField } from '@mui/material'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

export type NumberFieldProps = Omit<TextFieldProps, 'value' | 'onChange' | 'onKeyDown'> & {
  nonNegative?: boolean
  decimals?: number
  value?: number | null
  onChange?: (value: number | null) => void
}

export default function NumberField(props: NumberFieldProps) {
  const { decimals, value, onChange, onBlur: pOnBlur, nonNegative, InputProps, ...otherProps } = props
  const regExp = useMemo(() => getInputRegExp(decimals, nonNegative), [decimals, nonNegative])

  const [localValue, setLocalValue] = useState<string>('')

  const onLocalChange = useCallback(
    (newLocalValue: string) => {
      if (newLocalValue === '') {
        setLocalValue('')
        onChange?.(null)
        return
      }
      if (newLocalValue.match(regExp)) {
        setLocalValue(newLocalValue)
        const numberValue = toNumber(newLocalValue)
        onChange?.(numberValue)
      }
    },
    [regExp, onChange],
  )

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const key = event.key
      if (skipKey(key)) {
        return
      }
      const oldString = (event.target as any)?.value
      const position = (event.target as any)?.selectionStart
      const fString = futureString(key, position, oldString)
      if (fString === undefined || fString === null || fString === '') {
        return
      }
      if (!fString.match(regExp)) {
        event.preventDefault()
      }
    },
    [regExp],
  )

  const onBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>) => {
      const currentNumber = toNumber(localValue)
      const newString = toString(currentNumber, decimals)
      if (localValue !== newString) {
        setLocalValue(newString)
      }

      pOnBlur?.(event)
    },
    [pOnBlur, localValue, decimals],
  )

  useEffect(() => {
    if (value === undefined) {
      setLocalValue('')
      return
    }

    const valueNumber = value !== undefined ? value : null
    const localValueNumber = toNumber(localValue)
    const currentNumber = value === undefined ? localValueNumber : valueNumber
    const constrainedNumber = constrainNumber(currentNumber, nonNegative ? 0 : undefined)
    const decimalString = toString(constrainedNumber, decimals)

    const decimalNumber = toNumber(decimalString)

    if ((localValueNumber === null && decimalNumber !== null) || decimalNumber !== localValueNumber) {
      setLocalValue(decimalString)
    }
    if ((localValueNumber === null && decimalNumber !== null) || decimalNumber !== currentNumber) {
      onChange?.(decimalNumber)
    }
  }, [value, decimals, nonNegative])

  const textInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (textInputRef.current?.select) {
      textInputRef.current.select()
    }
  }, [localValue])

  return (
    <TextField //
      {...otherProps}
      value={localValue}
      onChange={(event) => onLocalChange(event.target.value)}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      onFocus={(e) => e.target.select()}
      InputProps={{ ...InputProps, ref: textInputRef }}
    />
  )
}

function getInputRegExp(decimals: number | undefined | null, notNegative: boolean | undefined): RegExp {
  const integerDigits = notNegative ? '\\d*' : '-?\\d*'
  if (decimals === undefined || decimals === null || Number.isNaN(decimals)) {
    return new RegExp(`^${integerDigits}(\\d[,.]\\d*)?$`)
  }
  if (decimals < 1) {
    return new RegExp(`^${integerDigits}$`)
  }
  const intDecimals = Math.floor(decimals)
  return new RegExp(`^${integerDigits}(\\d[,.]\\d{0,${intDecimals.toString()}})?$`)
}

function toString(number: number | null, decimals: number | undefined | null): string {
  if (number === null || Number.isNaN(number)) {
    return ''
  }
  if (decimals === undefined || decimals === null || Number.isNaN(decimals)) {
    return number.toString()
  } else {
    const intDecimals = Math.floor(decimals)
    return number.toFixed(intDecimals)
  }
}
function toNumber(string: string): number | null {
  if (string === '') {
    return null
  }
  const parsed = Number.parseFloat(string.replace(',', '.'))
  if (Number.isNaN(parsed)) {
    return null
  }
  return parsed
}

function constrainNumber(number: number | null, min: number | undefined): number | null {
  if (number === null || Number.isNaN(number)) {
    return null
  }
  if (min !== undefined && number < min) {
    return min
  }
  return number
}

function skipKey(key: string): boolean {
  return key ? key.length > 1 : true
}

function futureString(key: string, position: number, oldString: string): string {
  if (typeof oldString !== 'string') {
    return ''
  }

  if (
    key === undefined || //
    key === null ||
    position === undefined ||
    position === null ||
    Number.isNaN(position) ||
    oldString === undefined ||
    oldString === null
  ) {
    return oldString
  }
  let newChar = ''
  if (key.length === 1 || (key.length > 1 && /[^a-zA-Z0-9]/.test(key))) {
    newChar = key
  } else if (key === 'Spacebar') {
    newChar = ' '
  }

  const newString = [oldString.slice(0, position), newChar, oldString.slice(position)].join('')
  return newString
}
